<template>
  <v-layout wrap>
    <v-flex xs12 pb-0>
      <v-layout wrap align-center>
        <v-flex d-flex justify-space-between xs12 mt-3>
          <h1 class="headline">{{ $t('investor-report.title') }}</h1>
          <export-button
            v-if="$can('investor_report_export_report')"
            large
            action-name="investor-report/downloadReport"
            :file-name="$t('investor-report.title')"
            :query="filters"
          />
        </v-flex>
      </v-layout>

      <v-layout wrap align-center>
        <v-flex
          d-flex
          xs12
          sm12
          mt-4
          :class="{ 'mt-4': $vuetify.breakpoint.mdAndUp }"
        >
          <v-layout wrap align-center class="search-wrapper">
            <div class=" investor-report-search mb-5 d-flex">
              <v-text-field
                class="search-field"
                ref="search"
                v-model="searchString"
                :placeholder="$t('investor-report.placeholder')"
                autocomplete="off"
                solo
                hide-details
              />
              <v-btn
                type="button"
                class="search-btn warning"
                depressed
                large
                @click="filters.search = searchString"
              >
                {{ $t('common.btns.search') }}
              </v-btn>
            </div>
          </v-layout>
        </v-flex>

        <div style="width:100%" class="d-flex">
          <v-menu
            ref="menuDate"
            :close-on-content-click="false"
            v-model="menuDate"
            :return-value.sync="filters.dates"
            max-width="280px"
            min-width="280px"
            class="pr-4 "
            transition="scale-transition"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-on="on"
                v-model="filters.dates"
                prepend-icon="mdi-calendar"
                :label="$t('common.fields.rentDate')"
                readonly
                class="investor-report-dates"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="filters.dates"
              color="primary lighten-1"
              width="280"
              range
              :locale="$i18n.locale"
              :selected-items-text="filters.dates.toString()"
              :allowed-dates="allowedDates"
              @input="changeDate"
            ></v-date-picker>
          </v-menu>

          <payment-type-select
            v-if="$can('investor_report_show_payment_type')"
            v-model="filters.is_payment_official"
            class="investor-report-types ml-5"
            max-width="200px"
            :label="$t('investor-report.paymentType')"
            clearable
          />
          <status-select
            v-model="filters.status"
            class="investor-report-status ml-5"
          />

          <v-select
            v-model="filters.location"
            class="investor-report-location ml-5"
            item-text="title"
            item-value="id"
            :label="$t('auto.fields.location')"
            :loading="locationsLoading"
            :items="investorsLocations"
            clearable
          />

          <v-select
            class="ml-auto per-page"
            item-text="text"
            item-value="value"
            hide-details
            :label="$t('common.perPage')"
            :items="perPageItems"
            :value="perPage"
            @change="
              fetchList({ ...filters, page: currentPage, perPage: $event })
            "
          />
        </div>
      </v-layout>
    </v-flex>

    <v-flex sm12>
      <v-data-table
        ref="investors-table"
        class="investor-report-table"
        :height="tableHeight"
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="-1"
        hide-default-footer
      >
        <template #body="{ items }">
          <tr
            v-for="item in items"
            :key="item.id"
            :class="{ updating: updatingItems.includes(item.id) }"
          >
            <td>{{ item.num }}</td>
            <td>
              <router-link class="link-btn" :to="`${item.id}/info`">
                {{ item.user.name }}
              </router-link>
            </td>
            <td class="nowrap">{{ formatAmount(item.cars.length) }}</td>
            <td class="nowrap">{{ formatAmount(item.income_total) }}</td>
            <td class="nowrap">{{ formatAmount(item.expense_total) }}</td>
            <td v-if="$can('investor_report_show_commission')" class="nowrap">
              {{ formatAmount(item.income_total_commission) }}
            </td>
            <td class="nowrap">{{ formatAmount(item.payout) }}</td>
            <td class="nowrap">{{ formatAmount(item.balance) }}</td>
            <td v-if="$can('investor_report_show_payment_type')" class="nowrap">
              <edit-on-click-field
                :label="getPaymentTypeLabel(item.is_payment_official)"
              >
                <payment-type-select
                  :value="item.is_payment_official"
                  dense
                  autofocus
                  @change="
                    updateItem({
                      id: item.id,
                      params: { is_payment_official: $event },
                    })
                  "
                />
              </edit-on-click-field>
            </td>
            <td v-if="$can('investor_report_show_credit_card')">
              <button
                class="edit-btn"
                :disabled="!$can('investor_edit')"
                @click="
                  () => {
                    editDialog.opened = true;
                    editDialog.param = 'credit_cards_data';
                    editDialog.label = $t('investor-report.cardsData');
                    editDialog.value = item.credit_cards_data;
                    editDialog.itemId = item.id;
                    editDialog.type = 'text';
                  }
                "
              >
                {{ item.credit_cards_data || '—' }}
              </button>
            </td>
            <td v-if="$can('investor_report_show_inn')" class="nowrap">
              <button
                class="edit-btn"
                :disabled="!$can('investor_edit')"
                @click="
                  () => {
                    editDialog.opened = true;
                    editDialog.param = 'inn';
                    editDialog.label = $t('investor-report.inn');
                    editDialog.value = item.inn;
                    editDialog.itemId = item.id;
                    editDialog.type = 'text';
                  }
                "
              >
                {{ item.inn || '—' }}
              </button>
            </td>
            <td v-if="$can('investor_report_show_phone')" class="nowrap">
              {{ item.user.phone || '—' }}
            </td>
            <td v-if="$can('investor_report_show_ip_too')">
              <button
                class="edit-btn"
                :disabled="!$can('investor_edit')"
                @click="
                  () => {
                    editDialog.opened = true;
                    editDialog.param = 'ip_too';
                    editDialog.label = $t('investor-report.ip-too');
                    editDialog.value = item.ip_too;
                    editDialog.itemId = item.id;
                    editDialog.type = 'text';
                  }
                "
              >
                {{ item.ip_too || '—' }}
              </button>
            </td>
            <td>{{ item.user.location.title || '—' }}</td>
            <td>
              <div class="d-flex justify-center">
                <v-tooltip
                  v-if="$can('investor_report_payment')"
                  color="warning"
                  top
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      class="mr-3"
                      color="warning"
                      elevation="0"
                      dark
                      small
                      rounded
                      :disabled="paymentTypesLoading"
                      @click="
                        () => {
                          dialog.value = true;
                          dialog.item = item;
                          dialog.topUp = false;
                        }
                      "
                    >
                      <v-icon size="20">
                        mdi-account-cash
                      </v-icon>
                    </v-btn>
                  </template>
                  {{ $t('investor-report.pay') }}
                </v-tooltip>
                <v-tooltip
                  v-if="$can('investor_report_deposit')"
                  color="green"
                  top
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      class="mr-3"
                      color="green"
                      elevation="0"
                      small
                      dark
                      rounded
                      :disabled="paymentTypesLoading"
                      @click="
                        () => {
                          dialog.value = true;
                          dialog.item = item;
                          dialog.topUp = true;
                        }
                      "
                    >
                      <v-icon size="20">
                        mdi-cash-fast
                      </v-icon>
                    </v-btn>
                  </template>
                  {{ $t('investor-report.top-up') }}
                </v-tooltip>
                <v-tooltip
                  v-if="$can('investor_report_payment_view')"
                  color="info"
                  top
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      color="info"
                      elevation="0"
                      dark
                      small
                      rounded
                      :disabled="paymentTypesLoading"
                      @click="
                        () => {
                          historyDialog.value = true;
                          historyDialog.investorId = item.id;
                        }
                      "
                    >
                      <v-icon size="20">
                        mdi-clipboard-text-clock
                      </v-icon>
                    </v-btn>
                  </template>
                  {{ $t('investor-report.history-shortname') }}
                </v-tooltip>
              </div>
            </td>
          </tr>

          <tr
            v-if="items.length > 0"
            class="investor-report-table__totals grey lighten-3"
          >
            <td>
              <b>{{ $t('common.total') }}</b>
            </td>
            <td />
            <td>
              {{ carsTotalCount }}
            </td>
            <td>{{ getParamTotal('income_total') }}</td>
            <td>{{ getParamTotal('expense_total') }}</td>
            <td v-if="$can('investor_report_show_commission')">
              {{ getParamTotal('income_total_commission') }}
            </td>
            <td>{{ getParamTotal('payout') }}</td>
            <td>{{ getParamTotal('balance') }}</td>
            <td colspan="7" />
          </tr>
        </template>

        <template #footer>
          <v-divider />
          <div class="pt-1 pb-2">
            <v-pagination
              color="warning"
              :value="currentPage"
              :length="lastPage"
              dark
              circle
              @input="fetchList({ ...filters, page: $event, perPage: perPage })"
            />
          </div>
        </template>
      </v-data-table>
    </v-flex>

    <investor-report-dialog
      v-model="dialog.value"
      :item="dialog.item"
      :top-up="dialog.topUp"
      @updated="onTransactionUpdate"
    />

    <transactions-history-dialog
      v-model="historyDialog.value"
      :items="historyDialogItems"
      :investor-id="historyDialog.investorId"
      @updated="onTransactionUpdate"
    />

    <text-field-edit-dialog
      v-model="editDialog.opened"
      :param-name="editDialog.param"
      :label="editDialog.label"
      :currentValue="editDialog.value"
      :type="editDialog.type"
      @save="
        updateItem({
          id: editDialog.itemId,
          params: $event,
        })
      "
    />

    <v-snackbar v-model="snack.value" :color="snack.color" bottom right>
      {{ snack.message }}
    </v-snackbar>
  </v-layout>
</template>

<script>
import { mapState } from 'vuex';
import InvestorReportDialog from '@/components/investor-report/InvestorReportDialog.vue';
import TransactionsHistoryDialog from '@/components/investor-report/TransactionsHistoryDialog.vue';
import TextFieldEditDialog from '@/components/shared/TextFieldEditDialog.vue';
import PaymentTypeSelect from '@/components/investor-report/PaymentTypeSelect.vue';
import EditOnClickField from '@/components/shared/EditOnClickField.vue';
import ExportButton from '@/components/shared/ExportButton.vue';
import StatusSelect from '@/components/investor-report/StatusSelect.vue';
import calcTableHeight from '@/helpers/calcTableHeight';
import formatAmount from '@/helpers/formatAmount';

export default {
  components: {
    InvestorReportDialog,
    TransactionsHistoryDialog,
    TextFieldEditDialog,
    PaymentTypeSelect,
    EditOnClickField,
    StatusSelect,
    ExportButton,
  },
  data: () => ({
    loading: false,
    paymentTypesLoading: false,
    locationsLoading: false,
    updatingItems: [],
    searchString: '',
    menuDate: false,
    tableHeight: 'auto',
    dialog: {
      value: false,
      item: {},
      topUp: false,
    },
    editDialog: {
      opened: false,
      param: '',
      label: '',
      value: '',
      itemId: null,
      type: '',
    },
    historyDialog: {
      value: false,
      investorId: null,
    },
    filters: {
      search: '',
      dates: [],
      is_payment_official: '',
      status: 1,
    },
    snack: {
      value: false,
      color: null,
      message: null,
    },
  }),
  watch: {
    filters: {
      deep: true,
      handler(params) {
        if (params.dates.length !== 1) {
          this.fetchList(params);
        }
      },
    },
  },
  computed: {
    ...mapState({
      items: (state) => state['investor-report'].list,
      currentPage: (state) => state['investor-report'].currentPage,
      lastPage: (state) => state['investor-report'].lastPage,
      perPage: (state) => state['investor-report'].perPage,
      investorsLocations: (state) => state.investor.investorsLocations,
    }),
    headers() {
      const items = [
        {
          text: this.$t('common.number'),
          sortable: false,
        },
        {
          text: this.$t('investorsList.tableHeaders.name'),
          sortable: false,
        },
        {
          text: this.$t('investor-report.carsNumber'),
          sortable: false,
        },
        {
          text: this.$t('common.table.coming'),
          sortable: false,
        },
        {
          text: this.$t('dtp.statuses.repair'),
          sortable: false,
        },
        {
          text: this.$t('common.commission'),
          sortable: false,
          permission: 'investor_report_show_commission',
        },
        {
          text: this.$t('investor-report.gaveOut'),
          sortable: false,
        },
        {
          text: this.$t('investorList.remainder'),
          sortable: false,
        },
        {
          text: this.$t('investor-report.paymentType'),
          sortable: false,
          permission: 'investor_report_show_payment_type',
        },
        {
          text: this.$t('investor-report.cardsData'),
          sortable: false,
          permission: 'investor_report_show_credit_card',
        },
        {
          text: this.$t('investor-report.inn'),
          sortable: false,
          permission: 'investor_report_show_inn',
        },
        {
          text: this.$t('common.fields.phone'),
          sortable: false,
          permission: 'investor_report_show_phone',
        },
        {
          text: this.$t('investor-report.ip-too'),
          sortable: false,
          permission: 'investor_report_show_ip_too',
        },
        {
          text: this.$t('auto.fields.location'),
          sortable: false,
        },
        { text: '', value: 'actions', sortable: false },
      ];

      // eslint-disable-next-line
      const withPermissions = items.filter(({ permission }) => {
        return this.$can(permission);
      });

      return withPermissions;
    },
    historyDialogItems() {
      const investor = this.items.find(
        ({ id }) => id === this.historyDialog.investorId,
      );
      return investor ? investor.transactions : [];
    },
    perPageItems() {
      return [
        { text: this.$t('common.allItems'), value: 99999 },
        { text: '25', value: 25 },
      ];
    },
    today() {
      const date = new Date();
      const formatNum = (v) => (v < 10 ? `0${v}` : v);

      return `${date.getFullYear()}-${formatNum(
        date.getMonth() + 1,
      )}-${date.getDate()}`;
    },
    carsTotalCount() {
      return this.items.reduce((total, item) => {
        const carsCount = item.cars.length;
        const sum = carsCount + total;

        return sum;
      }, 0);
    },
  },
  methods: {
    formatAmount,
    changeDate(val) {
      if (val.length === 2) {
        this.$refs.menuDate.save(this.filters.dates);
      }
    },
    allowedDates(value) {
      const [from, to] = this.filters.dates;

      if (!from) return true;
      if (to) return true;

      const firstDate = new Date(from);
      const nextDate = new Date(value);

      return nextDate >= firstDate;
    },
    async fetchList(params) {
      this.loading = true;
      await this.$store.dispatch('investor-report/fetchList', params);
      this.loading = false;
    },
    async fetchPaymentTypes() {
      const types = this.$store.state['investor-report'].paymentTypes;

      if (types.length > 0) return;
      this.paymentTypesLoading = true;
      await this.$store.dispatch('investor-report/fetchPaymentTypes');
      this.paymentTypesLoading = false;
    },
    async fetchInvestorProperties() {
      this.locationsLoading = true;
      await this.$store.dispatch('investor/fetchInvestorsProperties');
      this.locationsLoading = false;
    },
    proccessResult({ success, error }) {
      if (success) {
        this.dialog.value = false;
      }

      const snack = {
        value: true,
        color: 'success',
        message: this.$t('common.saved'),
      };

      if (error) {
        snack.color = 'error';
        snack.message = this.$t('common.errors.APIerror');
      }

      this.snack = snack;
    },
    onTransactionUpdate(result) {
      if (result.success) {
        this.fetchList({
          ...this.filters,
          page: this.currentPage,
          perPage: this.perPage,
        });
      }

      this.proccessResult(result);
    },
    async updateItem({ id, params }) {
      this.updatingItems.push(id);
      const result = await this.$store.dispatch('investor-report/updateItem', {
        id,
        params,
      });

      if (result.success) {
        await this.fetchList({
          ...this.filters,
          page: this.currentPage,
          perPage: this.perPage,
        });
      }

      this.proccessResult(result);
      this.updatingItems = this.updatingItems.filter((item) => item !== id);
    },
    getParamTotal(param) {
      return this.items.reduce((total, item) => {
        if (item[param] > 0) {
          total += item[param]; // eslint-disable-line
        }

        return total;
      }, 0);
    },
    getPaymentTypeLabel(type) {
      switch (String(type)) {
        case '1':
          return this.$t('investor-report.paymentOfficial');
        case '0':
          return this.$t('investor-report.paymentUnofficial');
        default:
          return '—';
      }
    },
  },
  created() {
    this.fetchInvestorProperties();
    this.fetchPaymentTypes();
    this.filters.dates = [this.today, this.today];
  },
  mounted() {
    const table = this.$refs['investors-table'];
    this.tableHeight = calcTableHeight(table.$el);
  },
};
</script>

<style lang="scss">
.investor-report-dates,
.investor-report-types,
.investor-report-status,
.investor-report-location {
  max-width: 220px !important;
}

.investor-report-table {
  thead {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  th,
  .nowrap {
    white-space: nowrap;
  }

  .updating {
    opacity: 0.5;
    pointer-events: none;
  }

  .edit-btn {
    text-align: left;
    font-size: 14px;
    font-weight: 600;
    color: #464646;
    line-height: 1.3;

    &[disabled] {
      font-weight: normal;
    }

    &:not([disabled]):hover {
      opacity: 0.7;
    }
  }

  &__totals {
    position: sticky;
    bottom: 0;
  }
}
.investor-report-search {
  max-width: 450px;
  width: 100%;
}
.investor-report-search .search-field {
  max-width: 430px !important;
}
.date {
  max-width: 300px;
  padding: 15px;
}
.link-btn {
  color: #424242;
  font-weight: 600;
  text-decoration: underline;

  &:hover:not([disabled]) {
    text-decoration: none;
  }

  &[disabled] {
    opacity: 0.7;
  }
}
.per-page {
  max-width: 120px;
  margin-left: 30px;
}

.v-pagination__item,
.v-pagination__navigation {
  box-shadow: none;
}
</style>
